import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { reportEvent } from '~/domains/analytics';
import { UNIFIED_LOGIN_ERRORS } from '~/domains/auth/consts';
import useEORedirectUrl from '~/domains/auth/hooks/useEORedirectUrl';
import { useGetBranding } from '~/domains/auth/hooks/useGetBranding';
import { useRecaptcha } from '~/domains/auth/hooks/useRecaptcha';
import { Routes } from '~/domains/common/constants/routes';
import { showSplashNotification } from '~/domains/common/hooks/useShowSplashNotification';
import type { LogInInput } from '~/services/identity/unauthenticated/identity.service.api';
import { logIn } from '~/services/identity/unauthenticated/identity.service.api';
import type { APIAuthUserData } from '~/services/users/types';
import { me } from '~/services/users/users.service.api';

import { setTokens } from '../utils/setTokens';
import { removeTokens } from '../utils/removeTokens';

const useLogin = ({
  onSuccess,
}: {
  onSuccess?: (user: APIAuthUserData, redirectUrl?: string) => void;
}) => {
  const { setUser } = useContext(UserContext);
  const router = useRouter();
  const { getRecaptchaToken } = useRecaptcha();
  const { t } = useTranslation('auth');
  const { destination, platform } = useGetBranding();
  const { getEORedirectUrl } = useEORedirectUrl();

  const { mutate: handleLogin, isLoading: isLoggingIn } = useMutation({
    mutationKey: ['logIn'],
    mutationFn: async (logInPayload: LogInInput) => {
      const token = await getRecaptchaToken();
      const logInData = await logIn({
        ...logInPayload,
        token,
        destination,
        platform,
      });

      setTokens(logInData);

      const user = await me();
      const singleUseToken = logInData?.singleUseToken && typeof logInData.singleUseToken === 'object'
          ? logInData.singleUseToken.token
          : null;

      return {
        user,
        userId: logInData.id,
        singleUseToken,
      };
    },
    onSuccess: async ({ singleUseToken, user, userId }) => {
      const userData = user?.data;

      reportEvent('User > Login', {
        userId,
        brand: destination,
        platform,
        ...userData,
      });

      const redirectUrl = getEORedirectUrl(destination, platform, singleUseToken);

      /**
       * This is a temporary solution to avoid setting the user in the context and storage for EO Brand login
       * (this triggers a lot of other API calls and events)
       * EO Brand login only requires setting access & refresh token cookies
       *
       * @TODO Create a wholer separate EO AUTH sandbox to minimise complexity
       */
      if (!destination) {
        window.sessionStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }

      if (onSuccess) onSuccess(userData, redirectUrl);
    },
    onError: (error: AxiosError & { response?: { data?: { showSupportCTA?: boolean } } }) => {
      removeTokens();

      // @ts-expect-error: message exists on data object
      const errorMessage = error?.response?.data?.message;
      const errorStatus = error.response?.status;
      const showSupportCTA = error.response?.data?.showSupportCTA;
      // @ts-expect-error: error exists on data object
      const errorType = error.response?.data?.error;
      if (UNIFIED_LOGIN_ERRORS.includes(errorType)) {
        router.push(
          {
            pathname: '/account/reset-password',
            query: {
              email: errorMessage,
              automatedSend: 1,
              brand: destination,
              platform,
            },
          },
          '/account/reset-password'
        );
      } else {
        showSplashNotification({
          title: t('signIn.errors.title'),
          message:
            errorStatus !== 401 ? errorMessage : t('signIn.errors.messages.wrongCredentials'),
          color: 'red',
          autoClose: 5000,
          showSupportCTA,
        });
        // Redirect back to login page on error if the user is not on already on the login page
        if (router.pathname !== Routes.signIn()) {
          router.replace(
            Routes.signIn({
              redirectTo: `${router.query.redirectTo}`,
              brand: destination,
              platform,
            })
          );
        }
      }
    },
  });

  return { handleLogin, isLoggingIn };
};

export default useLogin;
